import { InfoIcon } from "lucide-react";
import { type ComponentPropsWithoutRef, forwardRef } from "react";
import { Focusable } from "react-aria-components";

import { Label } from "@/components/ui/field";
import { Heading } from "@/components/ui/heading";
import { Text } from "@/components/ui/text";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/components/ui/utils";

export function FormSection({
  title,
  description,
  actions,
  children,
  error,
  className,
}: {
  children?: React.ReactNode;
  title: React.ReactNode;
  description?: string;
  actions?: React.ReactNode;
  error?: string | boolean;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex flex-col border rounded-md divide-y bg-background",
        className,
        error && "border-destructive",
      )}
    >
      <div className="flex items-center justify-between gap-0 md:gap-6 p-4 flex-wrap md:flex-nowrap">
        <div className="w-full">
          <Heading displayLevel={3}>{title}</Heading>
          {description && (
            <Text size="1" muted>
              {description}
            </Text>
          )}
          {error && (
            <Text size="1" elementType="p" className="text-destructive">
              {error}
            </Text>
          )}
        </div>
        {actions && <div className="mt-2 md:mt-0">{actions}</div>}
      </div>
      {children}
    </div>
  );
}

export function FormField({
  title,
  description,
  children,
  error,
  htmlFor,
  className,
  helpText,
}: {
  title: React.ReactNode;
  description?: string;
  children?: React.ReactNode;
  error?: string | boolean;
  className?: string;
  htmlFor?: string;
  helpText?: React.ReactNode;
}) {
  return (
    <div>
      <div
        className={cn(
          "flex items-center gap-0 md:gap-6 justify-between flex-wrap md:flex-nowrap",
          className,
        )}
      >
        <FormLabel
          description={description}
          className={cn("w-full flex-1", error && "text-destructive")}
          error={!!error}
          htmlFor={htmlFor}
          helpText={helpText}
        >
          {title}
        </FormLabel>
        {children && <div className="mt-2 md:mt-0 shrink-0">{children}</div>}
      </div>
      {error && typeof error === "string" && (
        <Text size="1" elementType="p" className="text-destructive">
          {error}
        </Text>
      )}
    </div>
  );
}

export const FormLabel = forwardRef<
  HTMLLabelElement,
  Omit<ComponentPropsWithoutRef<typeof Label>, "error"> & {
    description?: string;
    error?: string | boolean;
    helpText?: React.ReactNode;
  }
>(({ description, error, helpText, ...props }, _ref) => (
  <div className="w-full md:w-auto">
    <div className="flex items-center gap-2">
      <Label {...props} className={cn("text-sm block", props.className)} />
      {helpText && (
        <TooltipTrigger delay={100}>
          <Focusable>
            <InfoIcon
              tabIndex={-1}
              className="hidden md:block text-muted-foreground size-3"
            />
          </Focusable>
          <Tooltip placement="right" className="p-2">
            {helpText}
          </Tooltip>
        </TooltipTrigger>
      )}
    </div>
    {description && (
      <Text size="1" muted>
        {description}
      </Text>
    )}
    {error && typeof error === "string" && (
      <Text size="1" elementType="p" className="text-destructive">
        {error}
      </Text>
    )}
  </div>
));
